/**
 * Agent Builder API client for ros-app.
 *
 * Ported from packages/web — ros-app always talks to the CF backend and
 * authenticates with a Bearer token (no Cognito cookie flow), so the service
 * is configured once by AgentsScreen with the app's apiBaseUrl + token.
 */
import type {AgentDefinition, ModelCatalogEntry, ToolCatalogItem, TestResult, AgentFormData} from '../components/agent-builder/types'
import {mapAgentDefinition, mapToolCatalogItem} from '../components/agent-builder/types'

class AgentBuilderService {
  private baseUrl = ''
  private token: string | null = null
  private fetcher: typeof fetch = fetch
  private onForbidden: (() => void) | null = null

  configure(baseUrl: string, token: string | null, fetcher: typeof fetch = fetch, onForbidden?: () => void): void {
    this.baseUrl = baseUrl
    this.token = token
    this.fetcher = fetcher
    this.onForbidden = onForbidden ?? null
  }

  private headers(contentType = 'application/json'): Record<string, string> {
    const h: Record<string, string> = {'Content-Type': contentType}
    if (this.token) h['Authorization'] = `Bearer ${this.token}`
    return h
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await this.fetcher(`${this.baseUrl}${path}`, {
      credentials: 'include',
      headers: this.headers(),
      ...init,
    })
    if (!response.ok) {
      if (response.status === 403) this.onForbidden?.()
      const error = await response.json().catch(() => ({error: 'Request failed'}))
      throw new Error((error as {error?: string}).error || `Request failed: ${response.status}`)
    }
    return response.json()
  }

  // ── Agent Definitions ──────────────────────────────────────────────────

  async getAgents(status?: string): Promise<AgentDefinition[]> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : ''
    const data = await this.request<{agents: Record<string, unknown>[]}>(`/api/agent-builder/agents${qs}`)
    return (data.agents || []).map(mapAgentDefinition)
  }

  async getAgent(slug: string): Promise<AgentDefinition> {
    const data = await this.request<{agent: Record<string, unknown>}>(`/api/agent-builder/agents/${encodeURIComponent(slug)}`)
    return mapAgentDefinition(data.agent)
  }

  async getModels(): Promise<ModelCatalogEntry[]> {
    const data = await this.request<{models: ModelCatalogEntry[]}>('/api/agent-builder/models')
    return data.models || []
  }

  async createAgent(form: AgentFormData): Promise<AgentDefinition> {
    const data = await this.request<{agent: Record<string, unknown>}>('/api/agent-builder/agents', {
      method: 'POST',
      body: JSON.stringify({
        slug: form.displayName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
        displayName: form.displayName,
        shortDescription: form.shortDescription,
        detailedDescription: form.detailedDescription,
        systemPrompt: form.systemPrompt,
        mascot: form.mascot || 'pop',
        icon: form.icon,
        tools: form.tools,
        modelProvider: form.modelProvider,
        modelId: form.modelId,
        temperature: form.temperature,
        maxOutputTokens: form.maxOutputTokens,
        maxToolTurns: form.maxToolTurns,
        intentDescription: form.intentDescription,
        intentKeywords: form.intentKeywords,
        acceptedFileTypes: form.acceptedFileTypes,
        outputFormat: form.outputFormat,
        sessionSticky: form.sessionSticky,
        guardrails: form.guardrails,
        accessType: form.accessType,
        accessConfig: form.accessConfig,
      }),
    })
    return mapAgentDefinition(data.agent)
  }

  async updateAgent(slug: string, form: Partial<AgentFormData>): Promise<AgentDefinition> {
    const data = await this.request<{agent: Record<string, unknown>}>(`/api/agent-builder/agents/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      body: JSON.stringify(form),
    })
    return mapAgentDefinition(data.agent)
  }

  async deleteAgent(slug: string): Promise<void> {
    await this.request<unknown>(`/api/agent-builder/agents/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
    })
  }

  async activateAgent(slug: string): Promise<void> {
    await this.request<unknown>(`/api/agent-builder/agents/${encodeURIComponent(slug)}/activate`, {
      method: 'POST',
    })
  }

  async testAgent(slug: string, message: string): Promise<TestResult> {
    return this.request<TestResult>(`/api/agent-builder/agents/${encodeURIComponent(slug)}/test`, {
      method: 'POST',
      body: JSON.stringify({message}),
    })
  }

  // ── Tool Catalog ───────────────────────────────────────────────────────

  async getTools(category?: string): Promise<ToolCatalogItem[]> {
    const qs = category ? `?category=${encodeURIComponent(category)}` : ''
    const data = await this.request<{tools: Record<string, unknown>[]}>(`/api/agent-builder/tools${qs}`)
    return (data.tools || []).map(mapToolCatalogItem)
  }

  // ── System Prompts (R2-backed) ─────────────────────────────────────────

  async getPrompt(slug: string): Promise<{prompt: string; source: 'r2' | 'inline'; r2Key: string | null}> {
    return this.request(`/api/agent-builder/agents/${encodeURIComponent(slug)}/prompt`)
  }

  async savePrompt(slug: string, prompt: string): Promise<void> {
    const res = await this.fetcher(`${this.baseUrl}/api/agent-builder/agents/${encodeURIComponent(slug)}/prompt`, {
      method: 'PUT',
      credentials: 'include',
      headers: this.headers('text/plain'),
      body: prompt,
    })
    if (!res.ok) {
      if (res.status === 403) this.onForbidden?.()
      const err = await res.json().catch(() => ({error: 'Failed'}))
      throw new Error((err as {error?: string}).error || 'Failed to save prompt')
    }
  }
}

const agentBuilderService = new AgentBuilderService()
export default agentBuilderService
